import {PaymentFrequencyType} from "./component/payment-frequency-selection-component.domain";
import {AssetConditionType} from "./component/asset-condition-component.domain";
import {AssetSelectionComponentValue} from "./component/asset-selection-component.domain";
import {LoanTermType} from "./component/loan-terms-selection-component.domain";
import {FinanceType} from "./component/finance-type-component.domain";
import {BrokerageSelectionType} from "./component/brokerage-selection-component.domain";
import {RatecardDetails, SearchCompanyByABNResult} from "./application-service.domain";
import {TransactionType} from "./component/transaction-type-component.domain";
import {TotalPaymentBreakupDialogData} from "./component/total-payment-breakup-dialog.domain";
import {AppCalculator, TermRate, TermRateForBusinessOverdraft} from "../app-calculator";
import {LoanPurposeValue} from "./component/loan-purpose-selection-component.domain";
import {CheckActiveOverdraftAccountDetails} from "./pismo.domain";


export interface CalculateAssetFinanceEstmationResult {
  terms: TermRate,
  calculator: AppCalculator,
  repayment: number,
  totalPaymentBreakupDialogData: TotalPaymentBreakupDialogData
}

export interface CalculateBusinessLoanEstimationResult {
  terms: TermRate,
  calculator: AppCalculator,
  repayment: number,
  totalPaymentBreakupDialogData: TotalPaymentBreakupDialogData
}

export interface CalculateBusinessOverdraftEstimationResult {
  term: TermRateForBusinessOverdraft,
  calculator: AppCalculator,
  earlyTerminationFee?: number,
  earlyTerminationFeePercent?: number,
};

export type AppCalculatorRouteData =
  Omit<AppCalculatorAssetFinanceData, 'businessSearchValue' | 'rateCard'> & {ABN: string, rateCardId: number} |
  Omit<AppCalculatorBusinessLoanData, 'businessSearchValue' | 'rateCard' > & {ABN?: string, rateCardId: number} |
  Omit<AppCalculatorBusinessOverdraftData, 'rateCard'> & {rateCardId: number}
;

export type AppCalculatorAssetFinanceData = {
  type: 'AssetFinance',
  paymentFrequencyType: PaymentFrequencyType | null,
  assetConditionType: AssetConditionType | null,
  assetSelectionValue:
    Pick<Exclude<AssetSelectionComponentValue, null>,
    'category' | 'type' | 'model' | 'year' | 'truckGrossVehicleWeight'> &
    {vehicles?: { goodretail?: number, newprice?: number}[]} &
    {vehicle?: { goodretail?: number, newprice?: number }},
   // (Omit<Exclude<AssetSelectionComponentValue, null>, 'vehicle' | 'vehicles'> & {vehicles?: { goodretail?: number, newprice?: number}[]} & { vehicle?: { goodretail?: number, newprice?: number } }) | null, //
  assetYear: number,
  loanTerm : number,
  financeType: FinanceType | null,
  loanAmount: number,
  businessSearchValue: {type: 'search-result', result?: Pick<SearchCompanyByABNResult, 'ABNAgeMonths' | 'GSTAgeMonths'>} | null, // BusinessSearchValue, //
  existingApplicationBureauReport: Pick<SearchCompanyByABNResult, 'ABNAgeMonths' | 'GSTAgeMonths'> | null, // SearchCompanyByABNResult | null, //
  propertyOwnership: boolean | null,
  docFeeFinanced: boolean | null,
  brokerOriginationFee: number,
  brokerage: number,
  adverseOnFile: boolean | null,
  equifaxDirectorScoreMeetsProductThreshold: boolean | null,
  // AZ-1651: yes/no from "Equifax Director Score above 700". Distinct from the 550 toggle.
  equifaxDirectorScoreAbove700?: boolean | null,
  privateSaleOrLeaseback: boolean | null,
  balloonPayment: number,
  rateCard: RatecardDetails | null,
  invoiceAmount: number,
  deposit: number,
  brokerageAmount?: number,
  docFee?: number,
  balloonPaymentAmount?: number,
  paymentType?: string,
  transactionType?: TransactionType,
  creditRateAdjustment?: number,
  repaymentType ?: string|null,
  manualInterestRate?: number | null,
  abnAgeAboveThresholdOverride?: boolean, // Override for ABN age >= 24 months threshold (avoids modifying actual ABN age)
}

export type AppCalculatorBusinessLoanData = {
  type: 'BusinessLoans',
  paymentFrequencyType: PaymentFrequencyType | null,
  loanTermType: LoanTermType | null,
  brokerageType: BrokerageSelectionType | null,
  loanAmount: number,
  businessSearchValue: {type: 'search-result', result?: Pick<SearchCompanyByABNResult, 'ABNAgeMonths' | 'GSTAgeMonths'>} | null, // BusinessSearchValue, //
  propertyOwner: boolean,
  brokerage?: number,
  adverseOnFile: boolean,
  // ZEN-818 follow-up: legacy uplift inputs retained for backward-compat only;
  // BL/OD calculator now reads equifaxDirectorScoreAbove700 / gstAgeMonths instead.
  lowEquifaxScore?: boolean,
  directorScore?: boolean,
  rateCard: RatecardDetails | null,
  bureauReport: Pick<SearchCompanyByABNResult, 'ABNAgeMonths' | 'GSTAgeMonths'> | null, // SearchCompanyByABNResult | null, //
  brokerageAmount?: number,
  docFeeFinanced?: boolean,
  docFee?: number,
  paymentType?: string,
  creditRateAdjustment?: number,
  hideBrokerage?: boolean,
  repaymentType ?: string|null,
  abnAgeAboveThresholdOverride?: boolean, // Override for ABN age >= 24 months threshold (avoids modifying actual ABN age)
  // ZEN-818 follow-up: tier-model inputs for Business Loans.
  // gstAgeMonths drives Tier 1 (premium, no continuity); gstAgeMonthsWithContinuity drives Tier 2/3.
  equifaxDirectorScoreAbove700?: boolean,
  gstAgeMonths?: number,
  gstAgeMonthsWithContinuity?: number,
}


export type AppCalculatorBusinessOverdraftData = {
  type: 'BusinessOverdraft',
  loanTermType: LoanTermType | null,
  propertyOwner: boolean,
  adverseOnFile: boolean,
  // ZEN-818 follow-up: legacy uplift inputs retained for backward-compat only;
  // BL/OD calculator now reads equifaxDirectorScoreAbove700 / gstAgeMonths instead.
  lowEquifaxScore?: boolean,
  directorScore?: boolean,
  rateCard: RatecardDetails | null,
  facilityLimit: number,
  brokerageAmount?: number,
  docFee?: number
  creditRateAdjustment?: number,
  anticipatedDrawdownAmount?: number,
  loanPurpose?: LoanPurposeValue | null,
  activeAccountDetails?: CheckActiveOverdraftAccountDetails | null,
  termRenewal?: boolean,
  // ZEN-818 follow-up: tier-model inputs for Business Overdraft.
  // gstAgeMonths drives Tier 1 (premium, no continuity); gstAgeMonthsWithContinuity drives Tier 2/3.
  equifaxDirectorScoreAbove700?: boolean,
  gstAgeMonths?: number,
  gstAgeMonthsWithContinuity?: number,
  additionalCommission?: boolean,
}


